import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { Breadcrumb, Card, Form, Input, Button, Switch, message, Typography } from 'antd';
import * as Icon from 'react-feather';
import {
  communityActionStarts,
  communityActionErrors,
  getCommunityByIdAction,
  updateCommunityAction,
} from '../../core/data/redux/modules/community/actions';
import { getCommunityById, updateCommunity } from '../../services/communityServices';

const { Title } = Typography;
const { TextArea } = Input;

const EditCommunity = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { currentCommunity, loading } = useSelector((state: any) => state.community);
  const [form] = Form.useForm();
  const [initialValues, setInitialValues] = useState<any>(null);

  useEffect(() => {
    if (id) {
      fetchCommunity();
    }
  }, [id]);

  useEffect(() => {
    if (currentCommunity) {
      const values = {
        name: currentCommunity.name,
        unique_name: currentCommunity.unique_name,
        guideline: currentCommunity.guideline,
        is_private: currentCommunity.is_private,
      };
      setInitialValues(values);
      form.setFieldsValue(values);
    }
  }, [currentCommunity, form]);

  const fetchCommunity = async () => {
    dispatch(communityActionStarts());
    try {
      const res = await getCommunityById(id!);
      if (res.error) throw new Error(res.message || 'Error fetching community');
      dispatch(getCommunityByIdAction(res.data));
    } catch (err: any) {
      dispatch(communityActionErrors(err.message));
      message.error(err.message);
    }
  };

  const onFinish = async (values: any) => {
    dispatch(communityActionStarts());
    try {
      const communityData = {
        id: id,
        ...values,
      };
      
      const res = await updateCommunity(communityData);
      if (res.error === true) throw new Error(res.message || 'Error updating community');
      
      dispatch(updateCommunityAction(res.data));
      message.success('Community updated successfully');
      navigate('/admin/community-management');
    } catch (err: any) {
      dispatch(communityActionErrors(err.message));
      message.error(err.message);
    }
  };

  if (loading && !currentCommunity) return <Card loading style={{ margin: 24 }} />;

  return (
    <div className="page-wrapper page-settings" style={{ paddingTop: 0 }}>
      <div className="content">
        <Breadcrumb style={{ marginBottom: 20 }}>
          <Breadcrumb.Item>
            <a href="/admin/community-management">Community Management</a>
          </Breadcrumb.Item>
          <Breadcrumb.Item>Edit Community</Breadcrumb.Item>
        </Breadcrumb>
        
        <Card>
          <Title level={4} style={{ marginBottom: 24 }}>Edit Community</Title>
          
          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            initialValues={initialValues}
          >
            <Form.Item
              label="Name"
              name="name"
              rules={[{ required: true, message: 'Please enter community name' }]}
            >
              <Input placeholder="Enter community name" />
            </Form.Item>

            <Form.Item
              label="Unique Name"
              name="unique_name"
              rules={[{ required: true, message: 'Please enter unique name' }]}
            >
              <Input placeholder="Enter unique name (e.g., my-community)" />
            </Form.Item>

            <Form.Item
              label="Guideline (Optional)"
              name="guideline"
            >
              <TextArea rows={4} placeholder="Enter community guidelines..." />
            </Form.Item>

            <Form.Item
              label="Private Community"
              name="is_private"
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>

            <Form.Item>
              <div style={{ display: 'flex', gap: 8 }}>
                <Button type="primary" htmlType="submit" icon={<Icon.Save size={16} />}>
                  Update Community
                </Button>
                <Button onClick={() => navigate('/admin/community-management')}>
                  Cancel
                </Button>
              </div>
            </Form.Item>
          </Form>
        </Card>
      </div>
    </div>
  );
};

export default EditCommunity;
